import { useCallback, useEffect, useRef, useState } from "react";

export function useCountUp(target: number, enabled: boolean, duration = 600): string {
    const [display, setDisplay] = useState("0.00");
    const rafRef = useRef<number>(0);
    const startRef = useRef<number | null>(null);
    const fromRef = useRef(0);
    const currentRef = useRef(0);
    const animatingTargetRef = useRef(0);
    const prevEnabledRef = useRef(false);
    const durationRef = useRef(duration);

    durationRef.current = duration;

    const animate = useCallback((timestamp: number) => {
        if (startRef.current === null) startRef.current = timestamp;
        const elapsed = timestamp - startRef.current;
        const progress = Math.min(elapsed / durationRef.current, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = fromRef.current + (animatingTargetRef.current - fromRef.current) * eased;
        currentRef.current = current;
        setDisplay(current.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        if (progress < 1) {
            rafRef.current = requestAnimationFrame(animate);
        }
    }, []);

    // Run on every (target, enabled) change. When target changes mid-flight
    // (or after completion), re-anchor `fromRef` to the current value and
    // restart the RAF — animation continues smoothly to the new target with
    // no visible snap or jitter.
    useEffect(() => {
        const wasEnabled = prevEnabledRef.current;
        prevEnabledRef.current = enabled;

        if (!enabled) {
            cancelAnimationFrame(rafRef.current);
            // When the consumer clears its input (target → 0), reset the
            // displayed value too, so a re-entry animates from 0.00 instead
            // of resuming from the last shown amount. When target is
            // non-zero (e.g., between pulse signals), keep the displayed
            // value so the next pulse can retarget smoothly.
            if (target === 0) {
                currentRef.current = 0;
                fromRef.current = 0;
                animatingTargetRef.current = 0;
                setDisplay("0.00");
            } else {
                fromRef.current = currentRef.current;
            }
            return;
        }

        // Already animating toward this exact target — nothing to do.
        if (animatingTargetRef.current === target && wasEnabled) {
            return;
        }

        // From-zero (after a clear) starts at 0; mid-flight retarget continues
        // from current value so the animation flows smoothly into the new
        // target without snapping.
        const fromZero = currentRef.current === 0;
        fromRef.current = fromZero ? 0 : currentRef.current;
        animatingTargetRef.current = target;
        startRef.current = null;
        cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(animate);

        return (): void => {
            cancelAnimationFrame(rafRef.current);
        };
    }, [enabled, target, animate]);

    return display;
}
